import FVector from '@data/geometry/FVector';
import { StageContext } from './Context';

/**
 * Block Output Snapshot
 *
 * For each block we store the Sensor IDs & the final values of the sensors that this
 * block has changed.
 *
 * Storing this kind of structure, instead of StageContext (which contains references)
 * is mandatory to avoid inconsistencies when changing values. Prior if the pipeline
 * was ran twice with a block that changed values (e.g. unobs), those values would compound.
 *
 * Only references weight 8 bytes total, with this approach we're at 28 bytes, so we're
 * 3.5xthe size (worst case scenario in which a block modifies ALL the sensor values),
 * but with the correct result.
 *
 * This means that with 20 layers, 10 blocks per layer & 80k sensor points we will have
 * in memory: 20 * 10 * 80'000 * 28 bytes = 448'000'000 bytes (~448MB).
 */
export type CachedBlockSnapshot = {
    inputAbsoluteMinMax: [number, number];

    // Sensor Data:
    sensorIds: Uint32Array;

    scalarPatchSensorIds: Uint32Array;
    scalarPatchValues: Float64Array;

    vectorPatchSensorIds: Uint32Array;
    vectorPatchValues: Float64Array;

    // Block-specific data:
    data: Map<symbol, any>;
    absoluteMinMax: [number, number];
    remappedMinMax: [number, number];
};

export class PipelineMemory {
    private snapshot?: CachedBlockSnapshot;

    get(): CachedBlockSnapshot | undefined {
        return this.snapshot;
    }

    store(snapshot: CachedBlockSnapshot): void {
        this.snapshot = snapshot;
    }

    clear(): void {
        this.snapshot = undefined;
    }

    getCachedContext(): CachedBlockSnapshot | undefined {
        return this.snapshot;
    }

    // =====================================
    // STORING & SNAPSHOT LOGICS:
    // =====================================

    storeContext(inputContext: StageContext, outputContext: StageContext): void {
        this.snapshot = this.createSnapshot(inputContext, outputContext);
    }

    restoreContext(globalContext: StageContext): StageContext | null {
        if (!this.snapshot) {
            return null;
        }
        return this.restoreFromSnapshot(this.snapshot, globalContext);
    }

    /**
     * Serializes the output of a block into a structured Snapshot, which will then be
     * deserialized and used as caching system.
     *
     * @param outputContext
     * @returns A Snapshot of the block.
     */
    private createSnapshot(inputContext: StageContext, outputContext: StageContext): CachedBlockSnapshot {
        const sensorIds = new Uint32Array(outputContext.sensors.length);
        const scalarPatchSensorIds: number[] = [];
        const scalarPatchValues: number[] = [];
        const vectorPatchSensorIds: number[] = [];
        const vectorPatchValues: number[] = [];

        // Create a snapshot with SensorId, Scalar & Vectorial values:
        for (let i = 0; i < outputContext.sensors.length; i++) {
            const sensor = outputContext.sensors[i];

            const sensorId = sensor.getID();
            sensorIds[i] = sensorId;

            scalarPatchSensorIds.push(sensorId);
            scalarPatchValues.push(sensor.getScalarValue());

            const currentVector = sensor.getVectorialResult();
            vectorPatchSensorIds.push(sensorId);
            vectorPatchValues.push(currentVector.x, currentVector.y, currentVector.z);
        }

        return {
            inputAbsoluteMinMax: [...inputContext.absoluteMinMax] as [number, number],
            sensorIds,
            scalarPatchSensorIds: Uint32Array.from(scalarPatchSensorIds),
            scalarPatchValues: Float64Array.from(scalarPatchValues),
            vectorPatchSensorIds: Uint32Array.from(vectorPatchSensorIds),
            vectorPatchValues: Float64Array.from(vectorPatchValues),
            data: new Map(outputContext.data),
            absoluteMinMax: [...outputContext.absoluteMinMax] as [number, number],
            remappedMinMax: [...outputContext.remappedMinMax] as [number, number],
        };
    }

    /**
     * Deserializes a Snapshot into a StageContext for the next pipeline Block input:
     *
     * @param cachedSnapshot
     * @param globalContext
     *
     * @returns StageContext to be used as input for the next Block.
     */
    private restoreFromSnapshot(cachedSnapshot: CachedBlockSnapshot, globalContext: StageContext): StageContext | null {
        if (!this.hasSameAbsoluteMinMax(cachedSnapshot.inputAbsoluteMinMax, globalContext.absoluteMinMax)) {
            return null;
        }

        const sensorById = new Map<number, StageContext['sensors'][number]>();

        // Store all the sensors in a map by ID, for easier access:
        for (const sensor of globalContext.sensors) {
            sensorById.set(sensor.getID(), sensor);
        }

        const outputSensors: StageContext['sensors'] = [];

        // Loop all the modified sensors (in the snapshot) and update their values:
        for (let i = 0; i < cachedSnapshot.sensorIds.length; i++) {
            const sensorId = cachedSnapshot.sensorIds[i];
            const sensor = sensorById.get(sensorId);
            if (!sensor) return null;

            outputSensors.push(sensor);

            // Update scalar:
            if (cachedSnapshot.scalarPatchSensorIds[i] === sensorId) sensor.setScalarValue(cachedSnapshot.scalarPatchValues[i]);

            // Update vectorial:
            if (cachedSnapshot.vectorPatchSensorIds[i] === sensorId) {
                const base = i * 3;
                sensor.setVectorialValue(
                    new FVector(
                        cachedSnapshot.vectorPatchValues[base],
                        cachedSnapshot.vectorPatchValues[base + 1],
                        cachedSnapshot.vectorPatchValues[base + 2]
                    )
                );
            }
        }

        return {
            sensors: outputSensors,
            data: new Map(cachedSnapshot.data),
            absoluteMinMax: [...cachedSnapshot.absoluteMinMax] as [number, number],
            remappedMinMax: [...cachedSnapshot.remappedMinMax] as [number, number],
            octTree: globalContext.octTree,
        };
    }

    private hasSameAbsoluteMinMax(left: [number, number], right: [number, number]): boolean {
        return left[0] === right[0] && left[1] === right[1];
    }
}
